import { useState, useEffect } from 'react';
import { FeedbackForm } from '@/components/FeedbackForm';
import { FeedbackSuccess } from '@/components/FeedbackSuccess';
import CustomFormRenderer from '@/components/CustomFormRenderer';
import CustomFormSuccess from '@/components/CustomFormSuccess';
import { FeedbackRequest, FeedbackResponse } from '@/types/feedback';
import { useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Lock, Loader2 } from 'lucide-react';

interface JwtPayload {
  productId: string;
  productName: string;
  callerId: string;
  scope: string;
  customFormId?: string;
  iat: number;
  exp?: number;
}

const Index = () => {
  const [requestData, setRequestData] = useState<FeedbackRequest | null>(null);
  const [responseData, setResponseData] = useState<FeedbackResponse | null>(null);
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [productInfo, setProductInfo] = useState<{ productId: string; productName: string } | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  
  // Custom form states
  const [customForm, setCustomForm] = useState<any>(null);
  const [customFormResponseId, setCustomFormResponseId] = useState<string | null>(null);
  const [isLoadingCustomForm, setIsLoadingCustomForm] = useState(false);
  const [customFormError, setCustomFormError] = useState<string | null>(null);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    
    const isDevelopment = import.meta.env.MODE === 'development';
    
    if (isDevelopment) {
      console.log('Token from URL:', token);
    }
    
    if (!token) {
      setTokenError('No access token provided');
      return;
    }

    try {
      const payload = jwtDecode<JwtPayload>(token);
      
      if (isDevelopment) {
        console.log('Decoded payload:', payload);
        console.log('Payload fields:', {
          productId: payload.productId,
          productName: payload.productName,
          scope: payload.scope,
          hasProductId: !!payload.productId,
          hasProductName: !!payload.productName,
          scopeMatch: payload.scope === 'feedback-submit'
        });
      }
      
      if (payload.productId && payload.productName && payload.scope === 'feedback-submit') {
        setJwtToken(token);
        setProductInfo({
          productId: payload.productId,
          productName: payload.productName
        });

        // Check if this is a custom form token
        if (payload.customFormId) {
          setIsLoadingCustomForm(true);
          setCustomFormError(null);
          
          // Use an async function inside useEffect
          const loadCustomForm = async () => {
            try {
              const API_BASE_URL = import.meta.env.VITE_API_URL;
              const url = `${API_BASE_URL}/custom-form-responses/validate/${payload.customFormId}/${payload.productId}`;
              console.log('API_BASE_URL:', API_BASE_URL);
              console.log('Calling URL:', url);
              const response = await fetch(url);
              
              if (!response.ok) {
                const errorData = await response.json();
                setCustomFormError(errorData.error || 'Failed to load custom form');
                return;
              }
              
              const formData = await response.json();
              setCustomForm(formData.form);
            } catch (error) {
              console.error('Error loading custom form:', error);
              setCustomFormError('Failed to load custom form. Please try again.');
            } finally {
              setIsLoadingCustomForm(false);
            }
          };
          
          loadCustomForm();
        }
      } else {
        setTokenError('Invalid token: missing required information');
      }
    } catch (e) {
      console.error('JWT decode error:', e);
      setTokenError('Invalid or expired access token');
    }
  }, [location.search]);

  const handleSubmissionSuccess = (request: FeedbackRequest, response: FeedbackResponse) => {
    setRequestData(request);
    setResponseData(response);
  };

  const handleReset = () => {
    setRequestData(null);
    setResponseData(null);
    setCustomFormResponseId(null);
  };

  const handleCustomFormSuccess = (responseId: string) => {
    setCustomFormResponseId(responseId);
  };

  // Show error page if token is missing or invalid
  if (tokenError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-destructive/5 via-destructive/10 to-destructive/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-0 bg-card/90 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Access Restricted
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              A valid access link is required to submit feedback
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-destructive/10 rounded-lg p-4 border border-destructive/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                <h3 className="text-lg font-semibold text-destructive">Access Error</h3>
              </div>
              <p className="text-destructive">{tokenError}</p>
            </div>

            <div className="text-center space-y-4">
              <p className="text-muted-foreground">
                This feedback form requires a valid access link. Please check your email or contact support if you believe you should have access.
              </p>
              <p className="text-sm text-muted-foreground">
                If you need assistance, please contact your administrator or support team.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show custom form success
  if (customFormResponseId) {
    return (
      <CustomFormSuccess
        responseId={customFormResponseId}
        onReset={handleReset}
      />
    );
  }

  // Show regular feedback success
  if (requestData && responseData) {
    return (
      <FeedbackSuccess 
        requestData={requestData}
        responseData={responseData}
        onReset={handleReset}
      />
    );
  }

  // Show loading state for custom form
  if (isLoadingCustomForm) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-border/70 bg-card/90 backdrop-blur-sm">
          <CardContent className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <div className="text-lg text-muted-foreground">Loading custom form...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show custom form error
  if (customFormError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-destructive/5 via-destructive/10 to-destructive/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-0 bg-card/90 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Custom Form Error
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Unable to load the custom form
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-destructive/10 rounded-lg p-4 border border-destructive/20">
              <p className="text-destructive">{customFormError}</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground">
                The custom form may not exist, may not be active, or may not belong to this product.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show custom form if available
  if (customForm) {
    return (
      <CustomFormRenderer
        form={customForm}
        jwtToken={jwtToken}
        onSubmissionSuccess={handleCustomFormSuccess}
      />
    );
  }

  // Show regular feedback form as fallback
  return <FeedbackForm 
    onSubmissionSuccess={handleSubmissionSuccess} 
    jwtToken={jwtToken} 
    productInfo={productInfo}
  />;
};

export default Index;
